import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const NavigationContext = createContext();

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) throw new Error('useNavigation must be used within NavigationProvider');
  return context;
};

export const NavigationProvider = ({ children }) => {
  const location = useLocation();
  const scrollPositions = useRef({});
  const pageStates = useRef({});

  // Restore scroll position on navigation
  useEffect(() => {
    const key = location.pathname + location.search;
    const savedPosition = scrollPositions.current[key];
    
    if (savedPosition !== undefined) {
      // Restore after render
      setTimeout(() => {
        window.scrollTo(0, savedPosition);
      }, 0);
    } else {
      window.scrollTo(0, 0);
    }

    // Save scroll position before unmount
    return () => {
      scrollPositions.current[key] = window.scrollY;
    };
  }, [location]);

  const savePageState = (key, state) => {
    const fullKey = location.pathname + ':' + key;
    pageStates.current[fullKey] = state;
    try {
      sessionStorage.setItem('pageStates', JSON.stringify(pageStates.current));
    } catch (e) {
      console.warn('Failed to save page state:', e);
    }
  };

  const getPageState = (key, defaultValue = null) => {
    const fullKey = location.pathname + ':' + key;
    if (pageStates.current[fullKey] !== undefined) {
      return pageStates.current[fullKey];
    }
    
    // Try to restore from sessionStorage
    try {
      const saved = sessionStorage.getItem('pageStates');
      if (saved) {
        const parsed = JSON.parse(saved);
        pageStates.current = parsed;
        return parsed[fullKey] !== undefined ? parsed[fullKey] : defaultValue;
      }
    } catch (e) {
      console.warn('Failed to restore page state:', e);
    }
    
    return defaultValue;
  };

  const clearPageState = (key) => {
    const fullKey = location.pathname + ':' + key;
    delete pageStates.current[fullKey];
    try {
      sessionStorage.setItem('pageStates', JSON.stringify(pageStates.current));
    } catch (e) {
      console.warn('Failed to clear page state:', e);
    }
  };

  return (
    <NavigationContext.Provider value={{ savePageState, getPageState, clearPageState }}>
      {children}
    </NavigationContext.Provider>
  );
};
